
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import logo from '@/assets/logo_EMA.png';
import { LanguageSwitcher } from '@/components/Layout/LanguageSwitcher';

const PreAuth: React.FC = () => {
  const { t } = useTranslation();
  const [organizationId, setOrganizationId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const typewriterRef = useRef<HTMLSpanElement>(null);

  const typewriterText = t('auth.welcomeToEma');

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < typewriterText.length) {
        if (typewriterRef.current) {
          typewriterRef.current.textContent = typewriterText.slice(0, index + 1);
        }
        index++;
      } else {
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [typewriterText]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!organizationId.trim()) {
      setError(t('auth.enterOrgId'));
      return;
    }

    // Special case: redirect to onboarding
    if (organizationId.trim().toLowerCase() === 'onboarding') {
      navigate('/onboarding');
      return;
    }

    setLoading(true);
    
    try {
      // Validate organization exists (case-insensitive lookup)
      const { data, error: orgError } = await supabase
        .from('organizations')
        .select('id, name, status')
        .ilike('name', organizationId.trim())
        .maybeSingle();

      console.log('Organization lookup result:', { data, orgError });

      if (orgError) {
        console.error('Database error:', orgError);
        // Try to proceed anyway if it's just fumbademo (hardcoded fallback)
        if (organizationId.trim().toLowerCase() === 'fumbademo') {
          localStorage.setItem('organizationId', '00000000-0000-0000-0000-000000000001');
          localStorage.setItem('organizationName', 'fumbademo');
          navigate('/login');
          return;
        }
        setError(t('auth.unableToVerifyOrg'));
        setLoading(false);
        return;
      }

      if (!data) {
        // Try to proceed anyway if it's just fumbademo (hardcoded fallback)
        if (organizationId.trim().toLowerCase() === 'fumbademo') {
          localStorage.setItem('organizationId', '00000000-0000-0000-0000-000000000001');
          localStorage.setItem('organizationName', 'fumbademo');
          navigate('/login');
          return;
        }
        setError(t('auth.orgNotFound'));
        setLoading(false);
        return;
      }

      if (data.status !== 'active') {
        setError(t('auth.orgInactive'));
        setLoading(false);
        return;
      }

      // Store organization ID (UUID) and name for later use
      localStorage.setItem('organizationId', data.id);
      localStorage.setItem('organizationName', data.name);
      navigate('/login');
    } catch (error: any) {
      console.error('Error validating organization:', error);
      // Try to proceed anyway if it's just fumbademo (hardcoded fallback for network issues)
      if (organizationId.trim().toLowerCase() === 'fumbademo') {
        localStorage.setItem('organizationId', '00000000-0000-0000-0000-000000000001');
        localStorage.setItem('organizationName', 'fumbademo');
        navigate('/login');
        return;
      }
      const errorMessage = error?.message?.includes('Failed to fetch')
        ? t('auth.networkError')
        : t('auth.unableToVerifyOrg');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-secondary/10 dark:from-background dark:via-primary/10 dark:to-secondary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Language switcher */}
        <div className="flex justify-end">
          <LanguageSwitcher />
        </div>
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="relative">
              <img
                src={logo}
                alt="EMA Logo"
                className="w-16 h-16"
              />
            </div>
          </div>
          
          {/* Typewriter Effect */}
          <div className="h-16 flex items-center justify-center">
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              <span ref={typewriterRef}></span>{organizationId ? ` - ${organizationId.split('-')[0]}` : ''}
              <span className="animate-pulse">|</span>
            </h1>
          </div>
          
          <p className="text-blue-100 text-sm">
            {t('auth.enterOrgIdPrompt')}
          </p>
        </div>

        {/* Organization Input Card */}
        <div 
          className="rounded-lg border-0"
          style={{
            background: 'linear-gradient(to right bottom, rgb(0, 0, 0), rgb(17, 24, 39), rgb(0, 0, 0))',
            borderBottomColor: 'rgb(30, 41, 59)',
            borderBottomWidth: '2px',
            borderBottomStyle: 'solid'
          }}
        >
          <div className="p-6">
            <h3 className="text-white text-center text-lg font-semibold mb-6">{t('auth.organizationAccess')}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="bg-red-500/10 border-red-500/20 text-white">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div>
                <Input
                  type="text"
                  placeholder={t('auth.enterOrgName')}
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  className="bg-white/20 border-white/30 text-white placeholder:text-blue-100 focus:border-white/50"
                  required
                  disabled={loading}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-white/20 hover:bg-white/30 text-white font-semibold py-3 border border-white/30"
                disabled={!organizationId.trim() || loading}
              >
                {loading ? t('auth.validating') : t('auth.continueToLogin')}
              </Button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-blue-200 text-xs">
            {t('auth.preAuthPoweredBy')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreAuth;
